import React, { useState } from 'react';
import { LogoSVG, WhatsAppIcon } from '../../components/Icons';
import { WA_NUMBER } from '../../data/constants';
import { usePageMeta } from '../../utils/usePageMeta';
import { promesaDeRespuesta, waLink } from '../../utils/whatsapp';
import WhatsAppButton from '../../components/WhatsApp/WhatsAppButton';
import { GoogleRatingBadge } from '../../components/Resenas/Resenas';
import HeroVariante, { useVarianteAnuncio } from '../../components/HeroVariante/HeroVariante';
import '../DerechoLaboral/DerechoLaboral.css';

const WA_MSG = 'Hola, vi su página de Derecho de Familia y tengo una consulta. ¿Pueden ayudarme?';

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface FAQGroup {
  label: string;
  items: FAQItem[];
}

const groups: FAQGroup[] = [
  {
    label: 'I. Pensión de Alimentos',
    items: [
      {
        id: 'demandar-alimentos',
        question: '¿Cómo puedo demandar pensión de alimentos y qué requisitos debo cumplir?',
        answer: (
          <div className="faq-body">
            <p>Para demandar, el primer requisito obligatorio es haber asistido a una <strong>mediación previa</strong>. Si no se logra un acuerdo, el mediador entrega un "Acta de Mediación Frustrada", que nos permite ingresar la demanda al tribunal.</p>
            <p>Además, se requiere:</p>
            <ul>
              <li>Certificado de nacimiento del hijo/a</li>
              <li>Acreditar los gastos (necesidades) del alimentario</li>
              <li>Acreditar la capacidad económica del demandado</li>
            </ul>
          </div>
        ),
      },
      {
        id: 'aumento-rebaja',
        question: '¿Cuándo puedo pedir aumento o rebaja de la pensión de alimentos?',
        answer: (
          <div className="faq-body">
            <p>Se puede solicitar un aumento o rebaja siempre que <strong>hayan variado las circunstancias</strong> que se tuvieron a la vista al momento de fijar la pensión original.</p>
            <p>Por ejemplo:</p>
            <ul>
              <li>Un aumento en las necesidades del hijo (gastos médicos, universitarios)</li>
              <li>Un cambio significativo en los ingresos del padre o madre que paga</li>
            </ul>
          </div>
        ),
      },
      {
        id: 'rebaja-registro-deudores',
        question: '¿Puedo pedir rebaja de pensión de alimentos si estoy en el Registro Nacional de Deudores?',
        answer: (
          <div className="faq-body">
            <p>La ley es estricta en este punto: en general, el tribunal <strong>no admitirá a tramitación</strong> una demanda de rebaja de pensión de alimentos si el alimentante se encuentra con inscripciones vigentes en el Registro Nacional de Deudores de Pensiones de Alimentos, a menos que se acrediten circunstancias excepcionales.</p>
          </div>
        ),
      },
      {
        id: 'gastos-ordinarios-extraordinarios',
        question: '¿Qué son los gastos ordinarios y extraordinarios en la pensión de alimentos?',
        answer: (
          <div className="faq-body">
            <ul>
              <li>
                <strong>Gastos Ordinarios:</strong> Son los gastos recurrentes y previsibles destinados a la subsistencia, como alimentación, vivienda, educación mensual y vestuario. Estos ya están incluidos en el monto mensual fijado.
              </li>
              <li>
                <strong>Gastos Extraordinarios:</strong> Son aquellos imprevistos o que ocurren una sola vez, como una cirugía de urgencia, tratamientos dentales específicos o gastos por enfermedad. Generalmente, se establece que estos gastos se paguen por mitades entre ambos padres, aunque puede establecerse un porcentaje diferente según los ingresos de cada uno.
              </li>
            </ul>
          </div>
        ),
      },
    ],
  },
  {
    label: 'II. Relación Directa y Regular (Visitas)',
    items: [
      {
        id: 'hijo-no-quiere-visitas',
        question: '¿Qué pasa si mi hijo/a no quiere cumplir con el régimen de visitas?',
        answer: (
          <div className="faq-body">
            <p>El derecho de relación directa y regular es un <strong>derecho del niño/a</strong>, pero también un deber de los padres. Si existe resistencia, es fundamental evaluar la edad y madurez del menor.</p>
            <p>Legalmente, el padre afectado debe:</p>
            <ul>
              <li>Dejar constancia en <strong>Comisaría Virtual</strong> por cada incumplimiento</li>
              <li>Solicitar al tribunal que se cumpla el régimen</li>
            </ul>
            <p>Esto puede derivar en apercibimientos o mediación familiar para revisar el régimen si es necesario para el bienestar del menor.</p>
          </div>
        ),
      },
    ],
  },
  {
    label: 'III. Filiación y Reconocimiento',
    items: [
      {
        id: 'pareja-reconocer-hijo',
        question: '¿Mi pareja actual puede reconocer a mi hijo si es él quien actúa como padre?',
        answer: (
          <div className="faq-body">
            <p>Legalmente, un niño solo puede tener <strong>dos progenitores inscritos</strong>. Si el padre biológico ya reconoció al hijo, su pareja actual no puede simplemente reconocerlo en el Registro Civil.</p>
            <p>En este caso, la vía legal correcta sería iniciar un proceso de <strong>impugnación y reclamación de paternidad</strong>, siempre que se cumplan los requisitos legales y se cuente con la asesoría de un abogado para velar por el interés superior del niño.</p>
          </div>
        ),
      },
    ],
  },
  {
    label: 'IV. Divorcio y Matrimonio',
    items: [
      {
        id: 'cese-convivencia',
        question: '¿Qué es el Cese de Convivencia y por qué es tan importante para el divorcio?',
        answer: (
          <div className="faq-body">
            <p>Es el documento que acredita la <strong>fecha exacta en que se terminó la vida en común</strong>. Para matrimonios celebrados después de noviembre de 2004, este documento es el único medio para contar el plazo legal requerido para el divorcio:</p>
            <ul>
              <li><strong>1 año</strong> para divorcio de mutuo acuerdo</li>
              <li><strong>3 años</strong> para divorcio unilateral</li>
            </ul>
          </div>
        ),
      },
      {
        id: 'compensacion-economica',
        question: '¿Qué es la Compensación Económica y quién tiene derecho a ella?',
        answer: (
          <div className="faq-body">
            <p>Es un monto que busca <strong>reparar el menoscabo económico</strong> de uno de los cónyuges que, por dedicarse al cuidado de los hijos o del hogar, no pudo trabajar o lo hizo en menor medida de lo que podía y quería.</p>
            <p className="faq-note">📌 Se solicita al momento del divorcio o nulidad.</p>
          </div>
        ),
      },
      {
        id: 'bien-familiar',
        question: '¿Qué significa que una propiedad sea declarada "Bien Familiar"?',
        answer: (
          <div className="faq-body">
            <p>Significa que, aunque la casa esté a nombre de un solo cónyuge, este <strong>no puede venderla, hipotecarla o arrendarla</strong> sin la autorización del otro.</p>
            <p className="faq-note">📌 El objetivo es proteger la residencia principal de la familia.</p>
          </div>
        ),
      },
    ],
  },
];

const DerechoFamilia: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const variante = useVarianteAnuncio('familia');

  // Con campaña, el mensaje llega diciendo a qué viene la persona.

  const waUrl = waLink(variante ? variante.mensaje : WA_MSG);


  usePageMeta(
    'Derecho de Familia en Concepción | Preguntas Frecuentes | Souza & Villa Abogadas',
    'Abogadas de familia en Concepción. Resolvemos tus dudas sobre pensión de alimentos, divorcio, visitas, compensación económica y más. Primera consulta gratuita.',
    '/areas/derecho-familia',
  );

  const toggle = (id: string) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allItems = groups.flatMap(g => g.items);

  return (
    <div className="dl-page">
      {/* Header */}
      <header className="dl-header">
        <a href="/" className="dl-logo">
          <LogoSVG size={44} />
          <div className="dl-logo-text">
            <span className="dl-logo-name">Souza &amp; Villa</span>
            <span className="dl-logo-tagline">Abogadas</span>
          </div>
        </a>
        <div className="dl-header-actions">
          <a href="/#areas" className="dl-back-link">← Volver al inicio</a>
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="dl-btn-wa">
            Consulta gratis
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="dl-hero">
        <div className="dl-hero-inner">
          {variante ? <HeroVariante v={variante} /> : (
            <>
              <div className="dl-hero-label">Área de práctica</div>
              <h1 className="dl-hero-title">Derecho de <em>Familia</em></h1>
              <p className="dl-hero-desc">
                Preguntas frecuentes, orientación legal y todo lo que necesitas saber sobre pensión de alimentos, divorcio, visitas y más.
              </p>
            </>
          )}
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-whatsapp dl-btn-wa-cta">
            <WhatsAppIcon size={17} /> Consulta gratuita
          </a>
          <div className="dl-hero-respuesta">{promesaDeRespuesta()} · Primera consulta gratis</div>
          <GoogleRatingBadge className="rating-badge--hero dl-hero-rating" />
        </div>
      </section>

      {/* FAQ */}
      <section className="dl-faq-section">
        <div className="dl-faq-intro">
          <p>Reunimos las preguntas más frecuentes que nos hacen nuestros clientes en materia de familia. Si tu caso no aparece aquí, escríbenos: <strong>la primera orientación es gratuita</strong>.</p>
        </div>

        <div className="dl-faq-list">
          {groups.map((group) => (
            <React.Fragment key={group.label}>
              <div className="dl-faq-group-label">{group.label}</div>
              {group.items.map((faq) => {
                const isOpen = openItems.has(faq.id);
                return (
                  <div
                    key={faq.id}
                    id={`faq-${faq.id}`}
                    className={`dl-faq-item${isOpen ? ' dl-faq-item--open' : ''}`}
                  >
                    <button
                      className="dl-faq-question"
                      onClick={() => toggle(faq.id)}
                      aria-expanded={isOpen}
                    >
                      <span>{faq.question}</span>
                      <span className="dl-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    <div className="dl-faq-answer">
                      <div className="dl-faq-answer-inner">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="dl-cta-section">
        <div className="dl-cta-inner">
          <div className="dl-cta-label">¿Tienes dudas sobre tu situación?</div>
          <h2 className="dl-cta-title">Revisamos tu caso <em>sin costo</em></h2>
          <p className="dl-cta-desc">
            Contáctanos por WhatsApp. Nuestras abogadas te orientarán de forma clara y directa sobre qué opciones tienes y qué pasos puedes seguir.
          </p>
          <div className="dl-cta-actions">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-whatsapp dl-btn-wa-cta">
              <WhatsAppIcon size={17} /> Escribir por WhatsApp
            </a>
            <a href="/#agendar" className="dl-btn-ghost">
              Agendar consulta
            </a>
          </div>
          <p className="dl-cta-note">
            Oficina en Concepción Centro y Lomas Coloradas · +56 9 3924 1385
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="dl-footer">
        <p>© 2026 Souza &amp; Villa Abogadas · <a href="/">svabogadas.cl</a></p>
      </footer>

      <WhatsAppButton />
    </div>
  );
};

export default DerechoFamilia;
